import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import logo from '@/assets/gaius-logo.webp';

const emailSchema = z.object({
  email: z.string().trim().email("Adresse email invalide").max(255),
});

export const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const fr = language === 'fr';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      emailSchema.parse({ email });
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message);
        return;
      }
    }

    setIsSubmitting(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setIsSubmitting(false);

    if (resetError) {
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: resetError.message });
    } else {
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30 flex items-center justify-center py-12 px-4">
      <SEOHead
        title={fr ? "Mot de passe oublié | Gaius" : "Forgot Password | Gaius"}
        description={fr ? "Réinitialisez votre mot de passe Gaius" : "Reset your Gaius password"}
      />

      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/">
            <img src={logo} alt="Gaius" className="h-16 lg:h-20 mx-auto mb-4" />
          </Link>
        </div>

        <Card className="border-border shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-foreground">
              {fr ? 'Mot de passe oublié' : 'Forgot Password'}
            </CardTitle>
            <CardDescription>
              {fr ? 'Entrez votre email pour recevoir un lien de réinitialisation' : 'Enter your email to receive a reset link'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="text-center space-y-4 py-4">
                <Mail className="w-12 h-12 mx-auto text-primary" />
                <p className="text-foreground font-medium">
                  {fr ? 'Email envoyé !' : 'Email sent!'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {fr
                    ? 'Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.'
                    : 'If an account exists with this email, you will receive a reset link.'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">{fr ? 'Email' : 'Email'}</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={error ? 'border-destructive' : ''}
                  />
                  {error && <p className="text-sm text-destructive">{error}</p>}
                </div>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {fr ? 'Envoyer le lien' : 'Send reset link'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="text-center space-y-2">
          <Link to={fr ? '/connexion' : '/auth'} className="text-sm text-primary hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" />
            {fr ? 'Retour à la connexion' : 'Back to login'}
          </Link>
          <br />
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
            ← {fr ? 'Retour à l\'accueil' : 'Return to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
};
